"use client";

import { useRef, useState } from "react";
import { useInView } from "motion/react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { cn } from "@marketing-sites/utils/cn";
import { FIT_TO_OBJECT_FIT, ALIGN_TO_OBJECT_POSITION } from "./object-fit";

// @rive-app/react-canvas is ~287KB + WASM — dynamically import the player
// so it's only fetched when the component scrolls into view, rather than
// being included in the page's initial JS bundle and fetched during hydration.
const RivePlayer = dynamic(() => import("./player"), {
  ssr: false,
});

// WebGL2 renderer — heavier, but the only one that renders feathered effects
// (soft drop shadows, blurs) instead of flattening them to hard fills. Loaded
// lazily and only when `renderer="webgl2"`, so canvas usages never pay for it.
const RiveWebGLPlayer = dynamic(() => import("./player-webgl"), {
  ssr: false,
});

export type RivePlaceholder = { src: string; alt: string; priority?: boolean };

// String literal types matching the @rive-app/react-canvas Fit and Alignment enums.
// Defined here to avoid importing the runtime library at the module level.
export type RiveFit =
  | "Cover"
  | "Contain"
  | "Fill"
  | "FitWidth"
  | "FitHeight"
  | "None"
  | "ScaleDown"
  | "Layout";

export type Align =
  | "Center"
  | "TopLeft"
  | "TopCenter"
  | "TopRight"
  | "CenterLeft"
  | "CenterRight"
  | "BottomLeft"
  | "BottomCenter"
  | "BottomRight";

export type RiveProps = React.ComponentProps<"div"> & {
  className?: string;
  dataTestId?: string;
  src: string;
  autoplay?: boolean;
  playOnInView?: boolean;
  /** With `playOnInView`, also pause when scrolled out of view and resume on re-entry. */
  pauseOffscreen?: boolean;
  innerClassName?: string;
  amountInView?: number;
  fit?: RiveFit;
  align?: Align;
  stateMachineName?: string;
  /**
   * Render a named artboard from the `.riv` instead of the file's default.
   * For responsive desktop/mobile artboards, render one `Rive` per artboard
   * and hide the other with CSS (see the hero and carousel), rather than
   * switching a single instance at runtime.
   */
  artboard?: string;
  placeholder?: RivePlaceholder;
  /**
   * Render this image *instead of* the animation — the player never mounts, so
   * neither the `.riv` nor the Rive WASM is fetched. For locales whose artboard
   * text has not been translated; resolve it with each app's `localizeRiveStill`
   * so an English locale (or a locale with no still yet) still gets the
   * animation. Distinct from `placeholder`, which only covers the load.
   */
  staticImage?: RivePlaceholder;
  /**
   * Rendering backend. `canvas` (default) is lighter; `webgl2` is required for
   * animations with feathered effects (soft drop shadows, blurs), which the
   * canvas renderer flattens into hard-edged fills.
   */
  renderer?: "canvas" | "webgl2";
};

export default function Rive({ renderer = "canvas", ...props }: RiveProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "200px 0px" });
  const [isRiveLoaded, setIsRiveLoaded] = useState(false);
  const Player = renderer === "webgl2" ? RiveWebGLPlayer : RivePlayer;

  const objectFit = FIT_TO_OBJECT_FIT[props.fit ?? "Contain"];
  const objectPosition =
    ALIGN_TO_OBJECT_POSITION[props.align ?? "BottomCenter"];

  return (
    <div
      ref={ref}
      data-testid={props.dataTestId}
      className={cn(
        "z-8 absolute inset-0 size-full overflow-hidden",
        props.className,
      )}
    >
      {props.staticImage ? (
        <Image
          src={props.staticImage.src}
          alt={props.staticImage.alt}
          fill
          priority={props.staticImage.priority}
          loading={props.staticImage.priority ? "eager" : "lazy"}
          fetchPriority={props.staticImage.priority ? "high" : "auto"}
          className={cn("size-full", objectFit, objectPosition)}
        />
      ) : (
        <>
          {props.placeholder && !isRiveLoaded && (
            <Image
              src={props.placeholder.src}
              alt={props.placeholder.alt}
              fill
              priority={props.placeholder.priority}
              loading={props.placeholder.priority ? "eager" : "lazy"}
              fetchPriority={props.placeholder.priority ? "high" : "auto"}
              className="size-full object-cover object-center"
            />
          )}
          {isInView ? (
            <Player {...props} onRiveLoad={() => setIsRiveLoaded(true)} />
          ) : null}
        </>
      )}
    </div>
  );
}
